<template>
  <div>
    <!-- Toolbar -->
    <div class="flex flex-wrap items-center justify-between pb-4 py-1">
      <div class="flex items-center space-x-4">
        <!-- Filter button + dropdown (only on Start Consultations tab) -->
        <div class="relative">
          <button
            id="dropdownRadioButton"
            class="inline-flex items-center text-gray-400 bg-white border border-gray-300 rounded-md px-6 py-2"
            type="button"
            @click="toggleDropdown"
          >
            <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z"/>
            </svg>
            Filters
            <svg class="w-2.5 h-2.5 ms-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 4 4 4-4"/>
            </svg>
          </button>

          <template v-if="currentTab === 'consultations-queue'">
            <div v-show="isDropdownOpen" class="absolute z-10 mt-2 w-80 p-4 bg-white border border-gray-300 rounded-lg shadow-lg">
              <div class="mb-4">
                <label for="dateRegistered" class="block mb-2 text-sm font-medium text-gray-900">Visit Date</label>
                <input type="date" id="dateRegistered" v-model="filters.date"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                  placeholder="YYYY-MM-DD" />
              </div>
              <div class="mb-4">
                <label for="gender" class="block mb-2 text-sm font-medium text-gray-900">Gender</label>
                <select id="gender" v-model="filters.gender"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                  <option value="" disabled selected>Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div class="flex">
                <button type="button" @click="closeDropdown"
                  class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow" aria-label="Close">
                  <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                  </svg>
                </button>
              </div>
              <div class="flex justify-end space-x-2">
                <button type="button" @click="cancelFilters"
                  class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
                <button type="submit" :disabled="searching" @click="applyFilters"
                  class="px-4 py-2 text-sm font-medium text-white bg-custom-yellow rounded-lg hover:bg-custom-yellow-dark">
                  {{ searching ? "Searching..." : "Filter" }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <!-- Search -->
        <div class="relative flex">
          <input type="text" v-model="filters.search_string" id="table-search"
            class="px-4 py-2 text-gray-700 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
            placeholder="Search" />
          <button type="submit" @click="applyFilters"
            class="bg-custom-yellow text-white rounded-r px-2 md:px-3 py-0 md:py-1">
            <svg class="w-6 h-6 text-gray-100 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Stats cards -->
    <div class="mt-2">
      <div class="flex gap-3">
        <div class="w-full px-6 sm:w-1/2 xl:w-1/2 border border-gray-200 rounded-md">
          <div class="flex items-center px-1 py-3 bg-white rounded-md shadow-sm">
            <div class="w-full">
              <div class="text-gray-500">Total Patients</div>
              <h4 class="text-2xl font-semibold text-gray-700">{{ totalPatients }}</h4>
            </div>
          </div>
        </div>
        <div class="w-full px-6 sm:w-1/2 xl:w-1/2 border border-gray-200 rounded-md">
          <div class="flex items-center px-1 py-3 bg-white rounded-md shadow-sm">
            <div class="w-full">
              <div class="text-gray-500">Consultations Queue</div>
              <h4 class="text-2xl font-semibold text-gray-700">{{ totalVisits }}</h4>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Unified queue tab -->
    <div class="tabs flex border-b my-4">
      <button
        class="px-4 py-2 -mb-px font-semibold border-b-2"
        :class="currentTab === 'consultations-queue'
          ? 'border-custom-yellow text-custom-blue' : 'text-gray-500'"
        @click="currentTab = 'consultations-queue'"
      >
        Consultations Queue
      </button>
    </div>

    <component
      :is="currentTabComponent"
      ref="currentTabComponent"
      :filters="filters"
      @search="handleSearchStatus"
      @closeDropdown="closeDropdown"
    />
  </div>
</template>

<script>
import ConsultationPatientQueueTab  from "../tabs/ConsultationPatientQueueTab.vue";
import apiClient from "@/services/api";

export default {
  name: "ConsultantDashboardPage",
  components: {
    ConsultationPatientQueueTab,
  },
  data() {
    return {
      currentTab:    "consultations-queue",
      totalPatients: "loading...",
      totalVisits:   "loading...",
      isDropdownOpen: false,
      filters: {
        date: "",
        gender: "",
        search_string: "",
      },
      searching: false,
    };
  },
  mounted() {
    this.getConsultationDashboardData();
  },
  computed: {
    currentTabComponent() {
      return "ConsultationPatientQueueTab";
    },
  },
  methods: {
    cancelFilters() {
      this.filters.date = "";
      this.filters.gender = "";
      this.filters.search_string = "";
      this.$refs.currentTabComponent.searchConsultations();
      this.isDropdownOpen = false;
    },
    applyFilters() {
      this.$refs.currentTabComponent.searchConsultations();
    },
    handleSearchStatus(event) {
      this.searching = event;
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    async getConsultationDashboardData() {
      try {
        const response = await apiClient.getConsultationDashboardData();
        if (response.success) {
          this.totalPatients = response.data.total_patients;
          this.totalVisits   = response.data.total_queue;
        }
      } catch (error) {
        this.$toast.error("Failed to fetch consultation data");
        console.log(error);
      }
    },
  },
};
</script>

<style lang="css" scoped></style>